import { useCallback, useEffect, useMemo, useState } from 'react';
import { addDays, format, parseISO, subDays } from 'date-fns';
import { enUS, nb } from 'date-fns/locale';
import {
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Loader2,
  Target,
  CalendarClock,
  Flag,
  Sparkles,
  Pencil,
  Trash2,
} from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import api from '../../utils/axiosConfig';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import ActivityPeekModal from '../ActivityPeekModal';
import RunningExerciseCard from '../training/RunningExerciseCard';
import RunningExerciseDetail from '../training/RunningExerciseDetail';
import UserRunningExerciseEditorModal from '../training/UserRunningExerciseEditorModal';
import RecordingModeBadge from '../training/RecordingModeBadge';
import RunningWorkoutViz from '../training/RunningWorkoutViz';
import LinkedPlannedActivityCard from './LinkedPlannedActivityCard';
import {
  buildTrainingDayDisplayEntries,
  LINKED_STRAVA_ACTIVITY_META_KEY,
} from './plannerUtils';

const MODAL_TYPE_ORDER = ['daily_brief', 'activity', 'planned_session', 'journal', 'race', 'goal', 'health_new_issue', 'health_follow_up'];

function parseItemMetadata(raw) {
  if (raw == null) return null;
  if (typeof raw === 'object') return raw;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function normalizeItem(row) {
  return {
    ...row,
    metadata: parseItemMetadata(row.metadata),
  };
}

function sortModalItems(items) {
  return [...items].sort((a, b) => {
    const ia = MODAL_TYPE_ORDER.indexOf(a.type_key);
    const ib = MODAL_TYPE_ORDER.indexOf(b.type_key);
    const ra = ia === -1 ? 99 : ia;
    const rb = ib === -1 ? 99 : ib;
    if (ra !== rb) return ra - rb;
    const ta = new Date(a.occurred_at).getTime();
    const tb = new Date(b.occurred_at).getTime();
    if (ta !== tb) return ta - tb;
    return (a.id || 0) - (b.id || 0);
  });
}

function LogItemCardActivity({ item, t, onOpenActivity }) {
  return (
    <div className="rounded-lg border border-border bg-card p-3 text-card-foreground shadow-sm">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-xs font-medium text-muted-foreground">{t('trainingLog.typeActivity')}</p>
          <p className="mt-0.5 text-sm font-semibold text-foreground">{item.title || t('trainingLog.untitled')}</p>
          {!!item.summary && <p className="mt-1 text-xs text-muted-foreground">{item.summary}</p>}
        </div>
        {!!item.strava_activity_id && (
          <Button type="button" size="sm" variant="secondary" className="shrink-0" onClick={() => onOpenActivity(item.strava_activity_id)}>
            <ExternalLink className="mr-1 h-3.5 w-3.5" />
            {t('trainingLog.viewActivity')}
          </Button>
        )}
      </div>
    </div>
  );
}

function LogItemCardDailyBrief({ item, t }) {
  const [open, setOpen] = useState(false);
  const hasLongBody = (item.body || '').length > 280;
  return (
    <div className="rounded-lg border border-border bg-card p-3 text-card-foreground shadow-sm">
      <div className="flex items-center gap-2 text-primary">
        <Sparkles className="h-4 w-4 shrink-0" />
        <p className="text-xs font-medium">{t('trainingLog.typeDailyBrief')}</p>
      </div>
      <p className="mt-1 text-sm font-semibold text-foreground">{item.title || t('trainingLog.untitled')}</p>
      {!!item.summary && <p className="mt-1 text-xs text-muted-foreground">{item.summary}</p>}
      {!!item.body && (
        <div className="mt-2">
          <p className={cn('whitespace-pre-wrap text-xs text-foreground', !open && hasLongBody && 'line-clamp-4')}>
            {item.body}
          </p>
          {hasLongBody && (
            <Button type="button" variant="ghost" size="sm" className="mt-1 h-7 px-2 text-xs" onClick={() => setOpen(!open)}>
              {open ? (
                <>
                  <ChevronUp className="mr-1 h-3 w-3" />
                  {t('trainingLog.showLess')}
                </>
              ) : (
                <>
                  <ChevronDown className="mr-1 h-3 w-3" />
                  {t('trainingLog.showMore')}
                </>
              )}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

function LogItemCardPlanned({ item, t, locale, onOpenRunningDetail, onOpenRunningEditor, onItemMutated }) {
  const meta = item.metadata;
  const ure = item.user_running_exercise;
  const ts = item.training_session;
  const hasUre = !!(item.user_running_exercise_id && ure);
  const ureSplits = hasUre && Array.isArray(ure.preview_splits) ? ure.preview_splits : [];
  const libSplits = ts?.preview_splits;
  /** Prefer instance splits when present; otherwise fall back to linked library preview (empty instance). */
  const splits = hasUre ? (ureSplits.length > 0 ? ureSplits : libSplits || []) : libSplits || [];
  const cardTitle = hasUre
    ? ure.title || item.title || ts?.name
    : item.title || ure?.title || ts?.name;
  const cardDescription = hasUre
    ? ure.description || item.summary || ts?.description
    : item.summary || ure?.description || ts?.description;
  const canDetail = !!(item.user_running_exercise_id || item.training_session_id);
  const canEdit = !!item.user_running_exercise_id;
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    if (!onItemMutated) return;
    setDeleting(true);
    try {
      await api.delete(`/api/training-log/items/${item.id}`);
      setConfirmDelete(false);
      await onItemMutated();
    } catch (e) {
      console.error(e);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="rounded-lg border border-border bg-card text-card-foreground shadow-sm">
      <div className="flex items-center gap-2 border-b border-border/60 px-3 pt-3 text-muted-foreground">
        <CalendarClock className="h-4 w-4 shrink-0" />
        <p className="text-xs font-medium">{t('trainingLog.typePlannedSession')}</p>
        <div className="ml-auto flex shrink-0 items-center gap-0.5">
          {canEdit && (
            <TooltipProvider delayDuration={300}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-muted-foreground hover:text-foreground"
                    onClick={() => onOpenRunningEditor?.(item.user_running_exercise_id, item)}
                    aria-label={t('runningExercise.editPlan')}
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom">{t('runningExercise.editPlan')}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          {!!onItemMutated && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-muted-foreground hover:text-destructive"
              onClick={() => setConfirmDelete(true)}
              aria-label={t('trainingLog.removePlannedSession')}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      </div>
      <div className="p-3 pt-2">
        <RunningExerciseCard
          title={cardTitle}
          description={cardDescription}
          splits={splits}
          vizHeightPx={36}
          onOpenDetail={
            canDetail
              ? () =>
                  onOpenRunningDetail?.({
                    mode: item.user_running_exercise_id ? 'instance' : 'library',
                    instanceId: item.user_running_exercise_id,
                    librarySessionId: item.training_session_id,
                    title: cardTitle,
                  })
              : undefined
          }
        />
        <p className="mt-1 text-[10px] text-muted-foreground">
          {format(new Date(item.occurred_at), 'PPp', { locale })}
        </p>
        {!!confirmDelete && (
          <div className="mt-3 rounded-md border border-destructive/40 bg-destructive/10 p-3">
            <p className="text-xs font-medium text-foreground">{t('trainingLog.deletePlannedConfirmTitle')}</p>
            <p className="mt-1 text-[10px] text-muted-foreground">{t('trainingLog.deletePlannedConfirmHint')}</p>
            <div className="mt-2 flex flex-wrap gap-2">
              <Button type="button" size="sm" variant="outline" disabled={deleting} onClick={() => setConfirmDelete(false)}>
                {t('common.cancel')}
              </Button>
              <Button type="button" size="sm" variant="destructive" disabled={deleting} onClick={handleDelete}>
                {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : t('trainingLog.removePlannedSession')}
              </Button>
            </div>
          </div>
        )}
        {((meta && typeof meta === 'object' && Object.keys(meta).length > 0) || (ure?.recording_mode && ure.recording_mode !== 'outdoor')) && (
          <div className="mt-2 flex flex-wrap gap-1">
            {meta && typeof meta === 'object' && Object.entries(meta)
              .filter(
                ([k]) =>
                  k !== 'planned_sport' &&
                  k !== LINKED_STRAVA_ACTIVITY_META_KEY &&
                  k !== 'from_strava_activity_link'
              )
              .map(([k, v]) => (
                <Badge key={k} variant="outline" className="text-[10px] font-normal">
                  {k}: {String(v)}
                </Badge>
              ))}
            {meta?.planned_sport && (
              <Badge variant="secondary" className="text-[10px] font-normal">
                {(() => {
                  const sk = `trainingLog.plannedSport.${meta.planned_sport}`;
                  const lbl = t(sk);
                  return lbl === sk ? String(meta.planned_sport) : lbl;
                })()}
              </Badge>
            )}
            <RecordingModeBadge mode={ure?.recording_mode} />
          </div>
        )}
      </div>
    </div>
  );
}

function LogItemCardRace({ item, t, locale }) {
  return (
    <div className="rounded-lg border border-border bg-card p-3 text-card-foreground shadow-sm">
      <div className="flex items-center gap-2 text-destructive">
        <Flag className="h-4 w-4 shrink-0" />
        <p className="text-xs font-medium">{t('trainingLog.typeRace')}</p>
      </div>
      <p className="mt-1 text-sm font-semibold text-foreground">{item.title || t('trainingLog.untitled')}</p>
      {!!item.summary && <p className="mt-1 text-xs text-muted-foreground">{item.summary}</p>}
      {item.ends_at && (
        <p className="mt-1 text-[10px] text-muted-foreground">
          {t('trainingLog.endsAt')}: {format(new Date(item.ends_at), 'PPp', { locale })}
        </p>
      )}
    </div>
  );
}

function LogItemCardGoal({ item, t }) {
  const meta = item.metadata;
  return (
    <div className="rounded-lg border border-border bg-card p-3 text-card-foreground shadow-sm">
      <div className="flex items-center gap-2 text-primary">
        <Target className="h-4 w-4 shrink-0" />
        <p className="text-xs font-medium">{t('trainingLog.typeGoal')}</p>
      </div>
      <p className="mt-1 text-sm font-semibold text-foreground">{item.title || t('trainingLog.untitled')}</p>
      {!!item.summary && <p className="mt-1 text-xs text-muted-foreground">{item.summary}</p>}
      {!!item.body && <p className="mt-2 whitespace-pre-wrap text-xs text-foreground">{item.body}</p>}
      {meta && meta.target_km != null && (
        <p className="mt-1 text-xs text-muted-foreground">
          {t('trainingLog.goalTargetKm')}: {meta.target_km} km
        </p>
      )}
    </div>
  );
}

function LogItemCardJournal({ item, t, onMutated, editable }) {
  const [editing, setEditing] = useState(false);
  const [draftTitle, setDraftTitle] = useState(item.title || '');
  const [draftBody, setDraftBody] = useState(item.body || '');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    setEditing(false);
    setConfirmDelete(false);
    setDraftTitle(item.title || '');
    setDraftBody(item.body || '');
  }, [item.id, item.title, item.body]);

  useEffect(() => {
    if (editing) setConfirmDelete(false);
  }, [editing]);

  const handleSave = async () => {
    if (!editable || !onMutated) return;
    setSaving(true);
    try {
      await api.patch(`/api/training-log/items/${item.id}`, {
        title: draftTitle.trim() ? draftTitle.trim().slice(0, 500) : null,
        body: draftBody.trim() ? draftBody.trim().slice(0, 32000) : null,
      });
      setEditing(false);
      await onMutated();
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!editable || !onMutated) return;
    setDeleting(true);
    try {
      await api.delete(`/api/training-log/items/${item.id}`);
      setConfirmDelete(false);
      await onMutated();
    } catch (e) {
      console.error(e);
    } finally {
      setDeleting(false);
    }
  };

  const cancelEdit = () => {
    setDraftTitle(item.title || '');
    setDraftBody(item.body || '');
    setEditing(false);
  };

  if (!editable) {
    return (
      <div className="rounded-lg border border-border bg-card p-3 text-card-foreground shadow-sm">
        <p className="text-xs font-medium text-muted-foreground">{t('trainingLog.typeJournal')}</p>
        <p className="mt-1 text-sm font-semibold text-foreground">{item.title || t('trainingLog.untitled')}</p>
        {!!item.body && <p className="mt-2 whitespace-pre-wrap text-xs text-foreground">{item.body}</p>}
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-border bg-card p-3 text-card-foreground shadow-sm">
      <div className="flex items-start justify-between gap-2">
        <p className="text-xs font-medium text-muted-foreground">{t('trainingLog.typeJournal')}</p>
        {!editing && (
          <div className="flex shrink-0 gap-0.5">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-muted-foreground hover:text-foreground"
              onClick={() => setEditing(true)}
              aria-label={t('common.edit')}
            >
              <Pencil className="h-3.5 w-3.5" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-muted-foreground hover:text-destructive"
              onClick={() => setConfirmDelete(true)}
              aria-label={t('common.delete')}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        )}
      </div>

      {editing ? (
        <div className="mt-2 space-y-2">
          <div>
            <Label className="text-[10px] text-muted-foreground">{t('trainingLog.entryTitle')}</Label>
            <Input
              value={draftTitle}
              onChange={(e) => setDraftTitle(e.target.value)}
              className="mt-1 h-9 text-sm"
              maxLength={500}
            />
          </div>
          <div>
            <Label className="text-[10px] text-muted-foreground">{t('trainingLog.entryBody')}</Label>
            <Textarea
              value={draftBody}
              onChange={(e) => setDraftBody(e.target.value)}
              className="mt-1 resize-none text-xs"
              rows={4}
              maxLength={32000}
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <Button type="button" size="sm" disabled={saving} onClick={handleSave}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : t('common.save')}
            </Button>
            <Button type="button" size="sm" variant="ghost" disabled={saving} onClick={cancelEdit}>
              {t('common.cancel')}
            </Button>
          </div>
        </div>
      ) : (
        <>
          <p className="mt-1 text-sm font-semibold text-foreground">{item.title || t('trainingLog.untitled')}</p>
          {!!item.body && <p className="mt-2 whitespace-pre-wrap text-xs text-foreground">{item.body}</p>}
        </>
      )}

      {!!confirmDelete && !editing && (
        <div className="mt-3 rounded-md border border-destructive/40 bg-destructive/10 p-3">
          <p className="text-xs font-medium text-foreground">{t('trainingLog.deleteJournalConfirmTitle')}</p>
          <p className="mt-1 text-[10px] text-muted-foreground">{t('trainingLog.deleteJournalConfirmHint')}</p>
          <div className="mt-2 flex flex-wrap gap-2">
            <Button type="button" size="sm" variant="outline" disabled={deleting} onClick={() => setConfirmDelete(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="button" size="sm" variant="destructive" disabled={deleting} onClick={handleDelete}>
              {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : t('common.delete')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function LogItemCard({ item, t, locale, onOpenActivity, onItemMutated, onOpenRunningDetail, onOpenRunningEditor }) {
  switch (item.type_key) {
    case 'activity':
      return <LogItemCardActivity item={item} t={t} onOpenActivity={onOpenActivity} />;
    case 'daily_brief':
      return <LogItemCardDailyBrief item={item} t={t} />;
    case 'planned_session':
      return (
        <LogItemCardPlanned
          item={item}
          t={t}
          locale={locale}
          onOpenRunningDetail={onOpenRunningDetail}
          onOpenRunningEditor={onOpenRunningEditor}
          onItemMutated={onItemMutated}
        />
      );
    case 'race':
      return <LogItemCardRace item={item} t={t} locale={locale} />;
    case 'goal':
      return <LogItemCardGoal item={item} t={t} />;
    case 'journal':
      return <LogItemCardJournal item={item} t={t} onMutated={onItemMutated} editable />;
    default:
      return <LogItemCardJournal item={item} t={t} editable={false} />;
  }
}

export default function TrainingLogDayModal({ open, onOpenChange, clusterItems, onDataChanged }) {
  const { t, language } = useLanguage();
  const locale = language === 'no' ? nb : enUS;

  /** Calendar day when the modal was opened (from the timeline cluster). */
  const initialDayKey = useMemo(() => {
    const items = clusterItems || [];
    if (!items.length) return null;
    const keys = items.filter((it) => it?.occurred_at).map((it) => format(new Date(it.occurred_at), 'yyyy-MM-dd'));
    if (!keys.length) return null;
    return keys.sort()[0];
  }, [clusterItems]);

  const [currentDayKey, setCurrentDayKey] = useState(null);

  const [dayItems, setDayItems] = useState([]);
  const [dayPosts, setDayPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showPostColumn, setShowPostColumn] = useState(false);
  const [showInlineForm, setShowInlineForm] = useState(false);
  const [newPostBody, setNewPostBody] = useState('');
  const [saving, setSaving] = useState(false);
  const [peekActivityId, setPeekActivityId] = useState(null);
  const [runDetailOpen, setRunDetailOpen] = useState(false);
  const [runDetail, setRunDetail] = useState({ mode: 'library', instanceId: null, librarySessionId: null, title: '' });
  const [runEditorOpen, setRunEditorOpen] = useState(false);
  const [runEditorExerciseId, setRunEditorExerciseId] = useState(null);
  const [runEditorLogItem, setRunEditorLogItem] = useState(null);
  const [linkSuggestions, setLinkSuggestions] = useState([]);
  const [linkSuggestionsLoading, setLinkSuggestionsLoading] = useState(false);
  const [linkBusyKey, setLinkBusyKey] = useState(null);


  const loadLinkSuggestions = useCallback(async (dateKey) => {
    if (!dateKey) return;
    setLinkSuggestionsLoading(true);
    try {
      const res = await api.get('/api/training-log/link-suggestions', { params: { date: dateKey } });
      setLinkSuggestions(res.data?.suggestions || []);
    } catch (e) {
      console.error(e);
      setLinkSuggestions([]);
    } finally {
      setLinkSuggestionsLoading(false);
    }
  }, []);

  const loadDay = useCallback(async (dateKey) => {
    if (!dateKey) return;
    setLoading(true);
    try {
      const res = await api.get('/api/training-log/day', { params: { date: dateKey } });
      const items = (res.data?.items || []).map(normalizeItem);
      const posts = res.data?.posts || [];
      setDayItems(items);
      setDayPosts(posts);
      if (posts.length > 0) {
        setShowPostColumn(true);
        setShowInlineForm(false);
      } else if (items.length > 0) {
        setShowPostColumn(true);
        setShowInlineForm(true);
      } else {
        setShowPostColumn(false);
        setShowInlineForm(false);
      }
      setNewPostBody('');
    } catch (e) {
      console.error(e);
      setDayItems([]);
      setDayPosts([]);
      setShowPostColumn(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open && initialDayKey) {
      setCurrentDayKey(initialDayKey);
    }
  }, [open, initialDayKey]);

  useEffect(() => {
    if (open && (!clusterItems || clusterItems.length === 0)) {
      onOpenChange(false);
    }
  }, [open, clusterItems, onOpenChange]);

  useEffect(() => {
    if (!open || !currentDayKey) return;
    loadDay(currentDayKey);
  }, [open, currentDayKey, loadDay]);

  useEffect(() => {
    if (!open || !currentDayKey) return;
    loadLinkSuggestions(currentDayKey);
  }, [open, currentDayKey, loadLinkSuggestions]);


  useEffect(() => {
    if (!open) {
      setPeekActivityId(null);
    }
  }, [open]);

  const sortedItems = useMemo(() => sortModalItems(dayItems), [dayItems]);
  const displayEntries = useMemo(() => buildTrainingDayDisplayEntries(sortedItems), [sortedItems]);

  const refreshDayAndNotify = useCallback(async () => {
    if (currentDayKey) await loadDay(currentDayKey);
    onDataChanged?.();
  }, [currentDayKey, loadDay, onDataChanged]);

  const submitPost = async () => {
    if (!currentDayKey || saving) return;
    setSaving(true);
    try {
      await api.post('/api/training-log/posts', {
        body: newPostBody,
        logDate: currentDayKey,
      });
      setNewPostBody('');
      setShowInlineForm(false);
      await loadDay(currentDayKey);
      onDataChanged?.();
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };


  const handlePlannerLinkWorkout = async (stravaActivityId, urexId) => {
    const key = `${stravaActivityId}-${urexId}`;
    setLinkBusyKey(key);
    try {
      await api.post(`/api/strava/activity/${stravaActivityId}/link-exercise`, {
        user_running_exercise_id: urexId,
      });
      if (currentDayKey) {
        await loadLinkSuggestions(currentDayKey);
      }
      await refreshDayAndNotify();
    } catch (e) {
      console.error(e);
    } finally {
      setLinkBusyKey(null);
    }
  };

  const handleAddPostClick = () => {
    setShowPostColumn(true);
    setShowInlineForm(true);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          priority="high"
          className="flex max-h-[min(88vh,900px)] max-w-[min(92vw,920px)] flex-col gap-0 overflow-hidden p-0"
        >
          <DialogHeader className="shrink-0 space-y-1 border-b border-border px-4 py-3 pr-12">
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8 shrink-0"
                disabled={!currentDayKey}
                onClick={() => {
                  if (!currentDayKey) return;
                  setCurrentDayKey(format(subDays(parseISO(`${currentDayKey}T12:00:00`), 1), 'yyyy-MM-dd'));
                }}
                aria-label={t('trainingLog.previousDay')}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <div className="min-w-0 flex-1 text-center">
                <DialogTitle className="text-base font-semibold leading-tight">
                  {currentDayKey
                    ? format(parseISO(`${currentDayKey}T12:00:00`), 'PPPP', { locale })
                    : '—'}
                </DialogTitle>
                <DialogDescription className="text-xs text-muted-foreground">
                  {t('trainingLog.dayNavigateCalendarHint')}
                </DialogDescription>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8 shrink-0"
                disabled={!currentDayKey}
                onClick={() => {
                  if (!currentDayKey) return;
                  setCurrentDayKey(format(addDays(parseISO(`${currentDayKey}T12:00:00`), 1), 'yyyy-MM-dd'));
                }}
                aria-label={t('trainingLog.nextDay')}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </DialogHeader>

          <div className="grid min-h-0 flex-1 gap-0 lg:grid-cols-2">
            {showPostColumn && (
              <div className="flex min-h-0 flex-col border-b border-border lg:border-b-0 lg:border-r lg:border-border">
                <div className="min-h-0 flex-1 overflow-y-auto p-4">
                  <p className="mb-2 text-xs font-medium text-muted-foreground">{t('trainingLog.dayPostsHeading')}</p>
                  {loading ? (
                    <div className="flex justify-center py-6">
                      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                    </div>
                  ) : (
                    <>
                      {!dayPosts.length && showInlineForm && (
                        <div className="mb-3 space-y-2">
                          <Label className="text-xs">{t('trainingLog.newDayPost')}</Label>
                          <Textarea
                            rows={3}
                            value={newPostBody}
                            onChange={(e) => setNewPostBody(e.target.value)}
                            className="resize-none text-xs"
                            placeholder={t('trainingLog.postPlaceholder')}
                          />
                          <div className="flex gap-2">
                            <Button type="button" size="sm" disabled={saving} onClick={submitPost}>
                              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : t('trainingLog.savePost')}
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                setShowInlineForm(false);
                                setNewPostBody('');
                                if (dayPosts.length === 0) setShowPostColumn(false);
                              }}
                            >
                              {t('common.cancel')}
                            </Button>
                          </div>
                        </div>
                      )}
                      <ul className="space-y-2">
                        {dayPosts.map((p) => (
                          <li
                            key={p.id}
                            className="rounded-md border border-border bg-muted/30 px-3 py-2 text-xs text-foreground"
                          >
                            <p className="text-[10px] text-muted-foreground">
                              {format(new Date(p.created_at), 'PPp', { locale })}
                            </p>
                            <p className={cn('mt-1 whitespace-pre-wrap', !p.body && 'italic text-muted-foreground')}>
                              {p.body?.trim() ? p.body : t('trainingLog.postEmptyBody')}
                            </p>
                          </li>
                        ))}
                      </ul>
                      {dayPosts.length > 0 && showInlineForm && (
                        <div className="mt-3 space-y-2 border-t border-border pt-3">
                          <Label className="text-xs">{t('trainingLog.newDayPost')}</Label>
                          <Textarea
                            rows={3}
                            value={newPostBody}
                            onChange={(e) => setNewPostBody(e.target.value)}
                            className="resize-none text-xs"
                            placeholder={t('trainingLog.postPlaceholder')}
                          />
                          <div className="flex gap-2">
                            <Button type="button" size="sm" disabled={saving} onClick={submitPost}>
                              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : t('trainingLog.savePost')}
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                setShowInlineForm(false);
                                setNewPostBody('');
                              }}
                            >
                              {t('common.cancel')}
                            </Button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
                {!loading && dayPosts.length > 0 && !showInlineForm && (
                  <div className="shrink-0 border-t border-border p-3">
                    <Button type="button" variant="outline" size="sm" className="w-full" onClick={() => setShowInlineForm(true)}>
                      {t('trainingLog.addPost')}
                    </Button>
                  </div>
                )}
              </div>
            )}

            <div
              className={cn(
                'flex min-h-0 flex-col',
                !showPostColumn && 'lg:col-span-2'
              )}
            >
              <div className="min-h-0 flex-1 overflow-y-auto p-4">
                {!showPostColumn && !loading && (
                  <div className="mb-3">
                    <Button type="button" variant="secondary" size="sm" onClick={handleAddPostClick}>
                      {t('trainingLog.addDayPost')}
                    </Button>
                  </div>
                )}
                {(linkSuggestionsLoading || linkSuggestions.length > 0) && (
                  <div className="mb-4 rounded-lg border border-border bg-muted/20 p-3">
                    <p className="mb-1 text-xs font-medium text-muted-foreground">
                      {t('trainingLog.linkSuggestionsHeading')}
                    </p>
                    <p className="mb-3 text-[11px] text-muted-foreground">{t('trainingLog.linkSuggestionsHint')}</p>
                    {linkSuggestionsLoading && linkSuggestions.length === 0 ? (
                      <div className="flex justify-center py-2">
                        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                      </div>
                    ) : (
                      <ul className="space-y-3">
                        {linkSuggestions.map((s) => (
                          <li key={s.strava_activity_id} className="rounded-md border border-border bg-card p-2">
                            <p className="text-xs font-medium text-foreground truncate">{s.activity_name || '—'}</p>
                            <div className="mt-2 grid gap-2 sm:grid-cols-1 md:grid-cols-3">
                              {(s.top_urex || []).map((u) => {
                                const busy = linkBusyKey === `${s.strava_activity_id}-${u.user_running_exercise_id}`;
                                return (
                                  <div
                                    key={u.user_running_exercise_id}
                                    className="flex min-w-0 flex-col gap-1 rounded border border-border/60 bg-muted/10 p-2"
                                  >
                                    <RunningWorkoutViz splits={u.preview_splits || []} heightPx={36} className="w-full" />
                                    <p className="text-[10px] text-muted-foreground truncate">{u.title || '—'}</p>
                                    <Button
                                      type="button"
                                      size="sm"
                                      variant="secondary"
                                      className="mt-auto h-7 text-xs"
                                      disabled={busy}
                                      onClick={() =>
                                        handlePlannerLinkWorkout(s.strava_activity_id, u.user_running_exercise_id)
                                      }
                                    >
                                      {busy ? (
                                        <Loader2 className="h-3 w-3 animate-spin" />
                                      ) : (
                                        t('trainingLog.linkActivityToWorkout')
                                      )}
                                    </Button>
                                  </div>
                                );
                              })}
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}

                <p className="mb-2 text-xs font-medium text-muted-foreground">{t('trainingLog.dayItemsHeading')}</p>
                {loading && !dayItems.length ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : displayEntries.length === 0 ? (
                  <p className="text-sm text-muted-foreground">{t('trainingLog.dayNoItems')}</p>
                ) : (
                  <ul className="space-y-3">
                    {displayEntries.map((entry) =>
                      entry.kind === 'combined' ? (
                        <li key={`combined-${entry.planned.id}-${entry.activity.id}`}>
                          <LinkedPlannedActivityCard
                            plannedItem={entry.planned}
                            activityItem={entry.activity}
                            t={t}
                            locale={locale}
                            onOpenActivity={(id) => setPeekActivityId(id)}
                            onItemMutated={refreshDayAndNotify}
                            onOpenRunningDetail={(payload) => {
                              setRunDetail(payload);
                              setRunDetailOpen(true);
                            }}
                            onOpenRunningEditor={(exerciseId, logItem) => {
                              setRunEditorExerciseId(exerciseId);
                              setRunEditorLogItem(logItem ?? null);
                              setRunEditorOpen(true);
                            }}
                          />
                        </li>
                      ) : (
                        <li key={entry.item.id}>
                          <LogItemCard
                            item={entry.item}
                            t={t}
                            locale={locale}
                            onOpenActivity={(id) => setPeekActivityId(id)}
                            onItemMutated={refreshDayAndNotify}
                            onOpenRunningDetail={(payload) => {
                              setRunDetail(payload);
                              setRunDetailOpen(true);
                            }}
                            onOpenRunningEditor={(exerciseId, logItem) => {
                              setRunEditorExerciseId(exerciseId);
                              setRunEditorLogItem(logItem ?? null);
                              setRunEditorOpen(true);
                            }}
                          />
                        </li>
                      )
                    )}
                  </ul>
                )}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <ActivityPeekModal activityId={peekActivityId} onClose={() => setPeekActivityId(null)} />

      <RunningExerciseDetail
        open={runDetailOpen}
        onOpenChange={setRunDetailOpen}
        mode={runDetail.mode}
        instanceId={runDetail.instanceId}
        librarySessionId={runDetail.librarySessionId}
        fallbackTitle={runDetail.title}
      />

      <UserRunningExerciseEditorModal
        open={runEditorOpen}
        onOpenChange={(o) => {
          setRunEditorOpen(o);
          if (!o) {
            setRunEditorExerciseId(null);
            setRunEditorLogItem(null);
          }
        }}
        exerciseId={runEditorExerciseId}
        trainingLogItemId={runEditorLogItem?.id ?? null}
        diaryOccurredAt={runEditorLogItem?.occurred_at ?? null}
        diaryMetadata={runEditorLogItem?.metadata ?? null}
        onSaved={refreshDayAndNotify}
      />
    </>
  );
}
